import React, { useMemo, useCallback } from 'react';
import { message, Button, Space, Tooltip } from 'antd';
import copy from 'copy-to-clipboard';
import { CopyOutlined, CloseOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import ExportSvg from '@src/icons/export.svg';
import { useLatest } from 'react-use';
import { cloneDeep, get } from 'lodash';
import * as ReactDnD from 'react-dnd';
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend';
import { QueryBuilder, update, findPath, add } from 'react-querybuilder';
import { QueryBuilderDnD } from '@react-querybuilder/dnd';
import { QueryBuilderAntD } from '@react-querybuilder/antd';
import DimensionField from './DimensionField';
import DimensionValue from './DimensionValue';
import RuleGroup from './RuleGroup';
import Operator from './Operator';
import {
  getOperators,
  getFieldSchema,
  generateRuleByFieldSchema,
  generateGroupByFieldSchema,
  getGroupedFieldsByCategory,
} from './utils';
import * as Styled from './styled';
import * as Types from '../../types';
import {
  type QueryBuilderContext,
  type Query,
  type QueryBuilderFieldSchema,
  type QueryBuilderGroupRule,
} from './types';
import 'react-querybuilder/dist/query-builder.css';
import { DataSource } from '../../utils';

export interface FilterRulesBuilderProps {
  dataSource: DataSource;
  query: Query;
  disabledOperators?: string[];
  showError?: boolean;
  initialField?: QueryBuilderFieldSchema;
  fields: QueryBuilderFieldSchema[];
  filterRules?: Types.Filter['rule'];
  drilldownLimit?: number;
  onChange: (query: Query) => void;
}

const operators = getOperators();

const FilterRulesBuilder = (builderProps: FilterRulesBuilderProps) => {
  const { dataSource, query, showError, fields, drilldownLimit = 900, onChange } = builderProps;

  const initialField = builderProps.initialField || fields[0];

  const groupedFields = useMemo(() => getGroupedFieldsByCategory(fields), [fields]);

  const handleQueryChangeRef = useLatest((q: Query) => {
    // Clean group with empty rules
    const nextQ = { ...q };
    nextQ.rules = nextQ.rules.filter((it) => {
      const isEmptyGroup =
        (it as QueryBuilderGroupRule).rules && !(it as QueryBuilderGroupRule).rules.length;
      return !isEmptyGroup;
    });
    onChange(nextQ);
  });

  const onKeyChange = useCallback<QueryBuilderContext['onKeyChange']>(
    (key, path) => {
      const newQuery = update(query, 'key' as any, key, path);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onFieldChange = useCallback<QueryBuilderContext['onFieldChange']>(
    (_, path, schema) => {
      const newQuery = cloneDeep(query);
      const target = findPath(path, newQuery);
      if (!target) return;
      const newFieldRule = generateRuleByFieldSchema(schema);
      Object.assign(target, newFieldRule);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onValueChange = useCallback<QueryBuilderContext['onValueChange']>(
    (data, path) => {
      const newQuery = update(query, 'value', data, path);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const onOperatorChange = useCallback<QueryBuilderContext['onOperatorChange']>(
    (operator, path) => {
      const newQuery = update(query, 'operator', operator, path);
      handleQueryChangeRef.current(newQuery as Query);
    },
    [query, handleQueryChangeRef],
  );

  const addRule = useCallback<QueryBuilderContext['addRule']>(
    (ruleOrGroup, parentPath) => {
      const newQuery = add(query, ruleOrGroup, parentPath ?? []);
      handleQueryChangeRef.current(newQuery);
    },
    [query, handleQueryChangeRef],
  );

  const handleAddAndRule = () => {
    const disabled = (query.rules || []).length >= drilldownLimit;
    if (disabled) {
      return message.warning(`Reached drilldown limit of ${drilldownLimit}`);
    }
    const rule = generateRuleByFieldSchema(initialField);
    addRule(rule, []);
  };

  const handleAddGroup = () => {
    const disabled = (query.rules || []).length >= drilldownLimit;
    if (disabled) {
      return message.warning(`Reached drilldown limit of ${drilldownLimit}`);
    }
    const orGroup = generateGroupByFieldSchema(initialField, 'or');
    addRule(orGroup, []);
  };

  const handleOnQueryChange = (q: Query) => {
    handleQueryChangeRef.current(q);
  };

  return (
    <Styled.Container>
      <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend }}>
        <QueryBuilderAntD>
          <QueryBuilder
            autoSelectField
            showCloneButtons
            context={{
              dataSource,
              fields,
              initialField,
              showError,
              groupedFields,
              onFieldChange,
              onKeyChange,
              onOperatorChange,
              onValueChange,
              addRule,
            }}
            fields={fields}
            query={query}
            operators={operators}
            onQueryChange={(q) => handleOnQueryChange(q as Query)}
            controlElements={{
              fieldSelector: DimensionField,
              operatorSelector: Operator,
              valueEditor: DimensionValue,
              ruleGroup: RuleGroup,
              combinatorSelector: () => null,
              cloneGroupAction: () => null,
              removeGroupAction: () => null,
              addGroupAction: () => null,
              addRuleAction: () => null,
              removeRuleAction: (props) => {
                return (
                  <Tooltip title="Remove filter rule">
                    <CloseOutlined onClick={props.handleOnClick} className="delete-icon" />
                  </Tooltip>
                );
              },
              cloneRuleAction: (props) => {
                const isValidFilterDimension = !!getFieldSchema(
                  get(props, ['ruleOrGroup', 'field']),
                  fields,
                );
                const renderCopyValuesIcon = () => {
                  const values = get(props, ['ruleOrGroup', 'value'], []);
                  if (!values.length) return null;

                  const handleCopyValues = () => {
                    const valueStr = values.map((it) => it.label).join(', ');
                    if (copy(valueStr)) {
                      message.info('Copied to clipboard');
                    }
                  };
                  return (
                    <Tooltip title="Copy values">
                      <CopyOutlined onClick={handleCopyValues} className="copy-icon" />
                    </Tooltip>
                  );
                };
                // invalid dimension don't have clone ability
                if (!isValidFilterDimension) {
                  return (
                    <>
                      <Tooltip title="The dimension has been deleted, please remove it">
                        <InfoCircleOutlined style={{ color: '#D0021B' }} className="prompt-icon" />
                      </Tooltip>
                      {renderCopyValuesIcon()}
                    </>
                  );
                }
                return (
                  <>
                    <Tooltip title="Clone filter rule">
                      <ExportSvg onClick={props.handleOnClick} className="clone-icon" />
                    </Tooltip>
                    {renderCopyValuesIcon()}
                  </>
                );
              },
            }}
          />
        </QueryBuilderAntD>
      </QueryBuilderDnD>
      <Space style={{ margin: '10px 0 0 60px' }}>
        <Button ghost type="primary" onClick={handleAddAndRule} icon={<PlusOutlined />}>
          AND
        </Button>
        <Button ghost type="primary" onClick={handleAddGroup} icon={<PlusOutlined />}>
          Group
        </Button>
      </Space>
    </Styled.Container>
  );
};

export default React.memo(FilterRulesBuilder);
